import React, { ReactElement, useCallback, useMemo } from "react";
import cx from "classnames";

// dls
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// widgets
import { AriaHidden, Button } from "@swiggy-private/core-ui";
import { FoodSymbolIconSvg } from "@cart-ui/widgets/food-symbol-icon";

// types
import { CartItemWithUUID, ItemImagesOnCartXPVariants } from "@swiggy-private/cart-types";

// helpers
import { priceInRupee } from "@cart-ui/helpers/Price";
import { isMerchItem } from "@cart-ui/helpers/Item";
import { isNonEmptyString } from "@swiggy-private/checkout-core";

// constants
import { TEXTS } from "@cart-ui/constants/index";
import { CART_ITEM_IMAGE_PLACEHOLDER, CART_ITEM_IMAGE_SIZE, TEXT } from "../constants";
import { CART_ITEMS_TEST_IDS } from "@swiggy-private/cart-test-ids";

import styles from "../styles/FoodItem.scss";

interface FoodItemTypeFreebieProps {
    children: ReactElement;
    item: CartItemWithUUID;
    disableActions: boolean;
    variant?: ItemImagesOnCartXPVariants;
    onItemImageLoad: () => void;
    removeItem: VoidFunction;
    isFreebieOptInItem?: boolean;
    isPopCart?: boolean;
}

const ReadTextBox = React.memo(function ReadTextBox({ message, className }: { message: string; className?: string }) {
    return (
        <div className={cx(styles.addButtonContainer, styles.addButtonContainerReadOnly, className)}>
            <Text variant="V2.body3.neutral.bold" className={styles.foodItemReadTextBox}>
                {message}
            </Text>
        </div>
    );
});

// TODO: add freebie item icon
export const FoodItemTypeFreebie = React.memo(function FoodItemTypeFreebie(
    props: FoodItemTypeFreebieProps,
): ReactElement {
    const isMerchItemFlag = useMemo(() => isMerchItem(props.item), [props.item]);

    const renderItemWithBigImage = useCallback(() => {
        const itemPrice = props.item.subtotal;
        const hasImage = isNonEmptyString(props.item.cloudinaryImageId);
        const actionsClassname = cx({
            [styles.foodItemActionsBig]: hasImage,
            [styles.foodItemActionsBigWithoutImage]: !hasImage,
        });
        const imageWrapperClassname = cx(styles.imageWraper, styles.imageWraperBig, {
            [styles.imageWraperBigNoImage]: !hasImage,
            [styles.imageWraperOutline]: hasImage,
        });

        return (
            <div className={styles.itemImageBigContainer}>
                <div className={styles.itemDetails}>
                    {!isMerchItemFlag && <FoodSymbolIconSvg vegType={props.item.isVeg} />}
                    {props.children}
                    <div className={cx(styles.freebie, styles.freebieBig)}>
                        <div className={styles.freebieGiftWrapper}>
                            <div>
                                <Text
                                    variant="V2.body2.neutral.medium"
                                    color="positive"
                                    className={styles.freebieTextBigImage}
                                >
                                    {TEXTS.FREE}
                                </Text>
                            </div>
                        </div>
                        {itemPrice > 0 && (
                            <AriaHidden as="span" className={styles.freebiePrice}>
                                {priceInRupee(itemPrice)}
                            </AriaHidden>
                        )}
                    </div>
                </div>
                <div className={imageWrapperClassname}>
                    {props.item.cloudinaryImageId && (
                        <Image
                            cloudinaryId={props.item.cloudinaryImageId}
                            cloudinaryWidth={CART_ITEM_IMAGE_SIZE.BIG.WIDTH * 2}
                            height={`${CART_ITEM_IMAGE_SIZE.BIG.HEIGHT}px`}
                            width={`${CART_ITEM_IMAGE_SIZE.BIG.WIDTH}px`}
                            className={cx(styles.itemImage, styles.itemImageBig)}
                            onLoad={props.onItemImageLoad}
                            data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_IMAGE}
                        />
                    )}

                    <ReadTextBox message={TEXTS.FREE} className={actionsClassname} />
                </div>
            </div>
        );
    }, [
        props.item.subtotal,
        props.item.cloudinaryImageId,
        props.item.isVeg,
        props.children,
        props.onItemImageLoad,
        isMerchItemFlag,
    ]);

    const renderItemWithSmallImage = useCallback(() => {
        const itemPrice = props.item.subtotal;
        const clodinaryId = isNonEmptyString(props.item.cloudinaryImageId)
            ? props.item.cloudinaryImageId
            : CART_ITEM_IMAGE_PLACEHOLDER;

        return (
            <>
                <div className={cx(styles.imageWraper, styles.imageWraperSmall)}>
                    <Image
                        cloudinaryId={clodinaryId}
                        cloudinaryWidth={CART_ITEM_IMAGE_SIZE.SMALL * 2}
                        height={`${CART_ITEM_IMAGE_SIZE.SMALL}px`}
                        width={`${CART_ITEM_IMAGE_SIZE.SMALL}px`}
                        className={styles.itemImage}
                        onLoad={props.onItemImageLoad}
                        data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_IMAGE}
                    />
                    {!isMerchItemFlag && (
                        <FoodSymbolIconSvg
                            vegType={props.item.isVeg}
                            className={cx(styles.symbol, styles.symbolForSmallImage)}
                        />
                    )}
                </div>
                {props.children}
                <div className={cx(styles.freebie, styles.freebieSmall)}>
                    <div className={styles.freebieGiftWrapper}>
                        <div className={styles.freebieGiftWrapper}>
                            <Text variant="V2.body2.neutral.medium" color="positive" className={styles.freebieText}>
                                {TEXTS.FREE}
                            </Text>
                        </div>
                    </div>
                    {itemPrice > 0 && !isMerchItemFlag && (
                        <AriaHidden as="span" className={styles.freebiePrice}>
                            {priceInRupee(itemPrice)}
                        </AriaHidden>
                    )}
                </div>
            </>
        );
    }, [
        props.children,
        props.item.cloudinaryImageId,
        props.item.isVeg,
        props.item.subtotal,
        props.onItemImageLoad,
        isMerchItemFlag,
    ]);

    const renderDefault = useCallback(() => {
        const itemPrice = props.item.subtotal;

        const freebieClassname = cx(styles.freebie, {
            [styles.freebieNoLeftMargin]: props.isFreebieOptInItem,
        });

        const removeButtonClassname = cx(styles.removeButton, {
            [styles.removeButtonPop]: props.isPopCart,
        });

        return (
            <>
                {props.children}
                {props.isFreebieOptInItem && (
                    <Button
                        dataTestId={CART_ITEMS_TEST_IDS.CART_ITEM_REMOVE_BUTTON}
                        className={removeButtonClassname}
                        onClick={props.removeItem}
                    >
                        {TEXT.REMOVE}
                    </Button>
                )}
                <div className={freebieClassname}>
                    {itemPrice > 0 && !isMerchItemFlag && (
                        <AriaHidden as="span" className={styles.freebiePrice}>
                            {priceInRupee(itemPrice)}
                        </AriaHidden>
                    )}
                    <div className={styles.freebieGiftWrapper}>
                        <div className={styles.freebieGiftWrapper}>
                            <Text variant="V2.body2.neutral.medium" color="positive" className={styles.freebieText}>
                                {TEXTS.FREE}
                            </Text>
                        </div>
                    </div>
                </div>
            </>
        );
    }, [
        props.children,
        props.item.subtotal,
        props.isFreebieOptInItem,
        props.isPopCart,
        props.removeItem,
        isMerchItemFlag,
    ]);

    return (
        <>
            {props.variant === ItemImagesOnCartXPVariants.BIG && renderItemWithBigImage()}
            {props.variant === ItemImagesOnCartXPVariants.SMALL && renderItemWithSmallImage()}
            {props.variant === ItemImagesOnCartXPVariants.DEFAULT && renderDefault()}
        </>
    );
});
